import React from 'react';
import { Link } from 'react-router-dom';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { RowsSkeleton } from './DashboardSkeleton';
import { Card, EmptyState, InlineError, ListRow, SectionHeader } from './ui';
import { valueTone } from './theme';
import { formatBeirutTime, formatMoney } from './format';
import { getGameNameWithFallback } from '../../utils/gameNameMapping';
import { BOOK_LABELS, bookLabel, providerLabel } from '../../utils/gameProviders';

const TOP_ROWS = 3;
const STALE_INGEST_MS = 30 * 60 * 1000;

const toMs = (timestamp) => {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value < 1e12 ? value * 1000 : value;
};

const byGgr = (rows) => [...(rows || [])].sort((a, b) => (b.ggr || 0) - (a.ggr || 0));

const Column = ({ title, rows }) => (
  <div className="min-w-0">
    <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500">{title}</h3>
    <div className="-mx-2">
      {rows.map((row) => (
        <ListRow key={row.key} label={row.label} value={row.value} valueClassName={row.tone} />
      ))}
    </div>
  </div>
);

/** Platform-wide casino and sportsbook figures from the game analytics ingest (Admins group only). */
export default function PlatformInsightsCard({ t, currency, platform, periodName, isLive, className = '' }) {
  const { status, data, error } = platform;
  const header = (
    <SectionHeader
      icon={Squares2X2Icon}
      title={t('platform', 'Platform')}
      subtitle={t('platformSubtitle', 'Whole platform · {{period}}', { period: periodName })}
      action={(
        <Link
          to="/admin-analytics"
          className="text-xs font-semibold text-teal-600 hover:text-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
        >
          {t('openAdminAnalytics', 'Details')}
        </Link>
      )}
    />
  );

  if (!data) {
    return (
      <Card className={className}>
        {header}
        {status === 'error' ? <InlineError>{error || t('platformFailed', 'Could not load platform figures.')}</InlineError> : <RowsSkeleton rows={3} />}
      </Card>
    );
  }

  const row = (key, label, ggr) => ({ key, label, value: formatMoney(ggr || 0, currency), tone: valueTone(ggr || 0) });
  const games = byGgr(data.casino?.games).slice(0, TOP_ROWS)
    .map((game) => row(`${game.gameId}-${game.currency}`, getGameNameWithFallback(game.gameId, game.gameId), game.ggr));
  const providers = byGgr(data.casino?.providers).slice(0, TOP_ROWS)
    .map((provider) => row(`${provider.provider}-${provider.currency}`, providerLabel(provider.provider), provider.ggr));
  const books = byGgr((data.sports?.books || []).filter((book) => BOOK_LABELS[book.book]))
    .map((book) => row(`${book.book}-${book.currency}`, bookLabel(book.book), book.ggr));
  const ingestAt = toMs(data.ingest?.lastTs);
  const ingestStale = isLive && ingestAt && Date.now() - ingestAt > STALE_INGEST_MS;

  return (
    <Card className={className}>
      {header}
      {games.length === 0 && providers.length === 0 && books.length === 0 ? (
        <EmptyState title={t('platformEmpty', 'No platform activity in this period')} />
      ) : (
        <>
          {ingestStale && (
            <p className="mb-2 text-[11px] text-amber-700 dark:text-amber-400">
              {t('platformDataAsOf', 'Data up to {{time}}', { time: formatBeirutTime(ingestAt) })}
            </p>
          )}
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-3">
            <Column title={t('topGames', 'Top games')} rows={games} />
            <Column title={t('topProviders', 'Top providers')} rows={providers} />
            <Column title={t('sportsbooks', 'Sportsbooks')} rows={books} />
          </div>
        </>
      )}
    </Card>
  );
}
